import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    Search,
    BookOpen,
    Zap,
    Users,
    Activity,
    Settings,
    Shield,
    Bot,
    ArrowRight,
    Play,
    Download,
    LineChart,
    Layers,
    ClipboardCheck,
    Network,
    CheckCircle2,
    ListTodo,
    Cpu,
    GraduationCap,
    FolderTree,
    Share2,
    Sparkles,
    Lock
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import { useTour, TourStep } from '../contexts/TourContext';

// Helper component for module progression cards
const ModuleCard: React.FC<{
    step: number;
    title: string;
    duration: string;
    icon: React.ReactNode;
    onClick: () => void;
    colorClass: string;
}> = ({ step, title, duration, icon, onClick, colorClass }) => {
    const { theme } = useAppContext();
    const isDark = theme === 'dark';

    return (
        <div className={`relative z-10 p-6 rounded-2xl border transition-all hover:-translate-y-1 overflow-hidden ${isDark ? 'bg-slate-900 border-slate-700 hover:border-blue-500/50 hover:shadow-[0_0_30px_rgba(59,130,246,0.15)]' : 'bg-white border-slate-200 hover:border-blue-300 hover:shadow-xl'}`}>
            <div className="flex justify-between items-start mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-lg bg-gradient-to-br ${colorClass}`}>
                    {icon}
                </div>
                <span className="flex items-center gap-1 text-xs font-bold text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-full">
                    Step {step}
                </span>
            </div>
            <h4 className="text-lg font-bold mb-2">{title}</h4>
            <div className="flex items-center gap-4 mb-6">
                <span className="text-xs font-semibold text-blue-500 flex items-center gap-1"><Play size={12} className="fill-blue-500" /> Interactive Tour</span>
                <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">{duration}</span>
            </div>
            <button
                onClick={onClick}
                className={`w-full py-2.5 rounded-xl text-sm font-bold transition-colors ${isDark ? 'bg-slate-800 text-white hover:bg-slate-700' : 'bg-slate-100 text-slate-900 hover:bg-slate-200'}`}
            >
                Launch Walkthrough
            </button>
        </div>
    );
};

const MagnusLearningCenter: React.FC = () => {
    const { theme } = useAppContext();
    const isDark = theme === 'dark';
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const { startTour, stopTour } = useTour();

    const [completedModules, setCompletedModules] = useState<string[]>(() => {
        const saved = localStorage.getItem('magnus-completed-modules');
        return saved ? JSON.parse(saved) : [];
    });

    const markModuleComplete = (moduleId: string) => {
        if (!completedModules.includes(moduleId)) {
            const newCompleted = [...completedModules, moduleId];
            setCompletedModules(newCompleted);
            localStorage.setItem('magnus-completed-modules', JSON.stringify(newCompleted));
        }
    };

    const startInteractiveTour = (moduleId: string, path: string, steps: TourStep[]) => {
        stopTour(); // Stop any existing tour
        navigate(path);
        // Delay to ensure the target view mounts fully before the tour tries to find elements
        setTimeout(() => {
            startTour(steps);
            // In a real app, we might wait for the tour to finish, 
            // but for this UX we'll mark it started/seen
            markModuleComplete(moduleId);
        }, 600);
    };

    // ----- HIGHLY SPECIFIC WALKTHROUGH TOURS -----

    const DESIGN_CONTEXT_TOUR: TourStep[] = [
        {
            target: 'body',
            title: 'Welcome to Requirements & Context',
            content: 'Defining the scope (5T) and context is the mandatory Step 1 in the AIAG-VDA process.',
            placement: 'center',
            page: '/customer-requirements'
        },
        {
            target: 'button:contains("Add Requirement")',
            title: '1. Ingest Requirements',
            content: 'Click here to manually add customer requirements (CSRs). This forms the foundation of your analysis.',
            placement: 'bottom'
        },
        {
            target: 'select',
            title: '2. Filter by Customer',
            content: 'Use this dropdown to isolate requirements for a specific OEM or Tier 1 client.',
            placement: 'bottom'
        }
    ];

    const PBS_MANAGER_TOUR: TourStep[] = [
        {
            target: 'body',
            title: 'Welcome to Architecture (PBS)',
            content: 'The Product Breakdown Structure represents Step 2 (Structure Analysis). Here you decompose your system into manageable parts.',
            placement: 'center',
            page: '/pbs-manager'
        },
        {
            target: 'button:contains("Add Manually")',
            title: '1. Add a Component',
            content: 'Click this button to define a new physical component in your system architecture.',
            placement: 'bottom'
        },
        {
            target: 'button:contains("Import from BOM")',
            title: '2. Bulk Import',
            content: 'Alternatively, click here to instantly map over an existing engineering Bill of Materials.',
            placement: 'bottom'
        }
    ];

    const FMEA_EDITOR_TOUR: TourStep[] = [
        {
            target: 'body',
            title: 'Welcome to the FMEA Editor',
            content: 'This is where you execute Step 3 (Function) through Step 6 (Optimization). Let\'s walk through making a compliant entry.',
            placement: 'center',
            page: '/fmea-editor'
        },
        {
            target: 'button:has(.lucide-plus)',
            title: '1. Add New Row',
            content: 'Click the "+" button to insert a blank analysis line into the workspace.',
            placement: 'bottom',
        },
        {
            target: 'table',
            title: '2. Failure Analysis',
            content: 'Click into the cells to define Functions, Failure Modes, and Effects. This is Step 4 of the AIAG-VDA methodology.',
            placement: 'top'
        }
    ];

    const SYSTEM_DEFINITION_TOUR: TourStep[] = [
        {
            target: 'body',
            title: 'Welcome to System Definition',
            content: 'Step 3: Function Mapping. Map your physical requirements into functional requirements for analysis.',
            placement: 'center',
            page: '/system-definition'
        },
        {
            target: 'button:contains("New Component")',
            title: '1. Manage Parts',
            content: 'Add or edit physical components in the BOM here.',
            placement: 'bottom'
        },
        {
            target: 'table',
            title: '2. Component Breakdown',
            content: 'Ensure every physical part is mapped to the structure correctly.',
            placement: 'top'
        }
    ];

    const ACTION_TRACKING_TOUR: TourStep[] = [
        {
            target: 'body',
            title: 'Welcome to Action Tracking',
            content: 'Step 6: Optimization. Drive risk down by tracking mitigation actions here.',
            placement: 'center',
            page: '/action-tracking'
        },
        {
            target: 'button:contains("My Tasks")',
            title: '1. Filter Tasks',
            content: 'View actions assigned specifically to you.',
            placement: 'bottom'
        },
        {
            target: 'table',
            title: '2. Track Progress',
            content: 'Monitor RPN reduction and update statuses from this table.',
            placement: 'top'
        }
    ];

    const EXPORT_TOUR: TourStep[] = [
        {
            target: 'body',
            title: 'Welcome to Export Manager',
            content: 'Step 7: Results Documentation. Securely export your risk models into CSVs.',
            placement: 'center',
            page: '/export'
        },
        {
            target: 'button:contains("Select All")',
            title: '1. Select Scope',
            content: 'Choose which elements to include in the compliance export.',
            placement: 'bottom'
        },
        {
            target: 'button:contains("Export CSV")',
            title: '2. Generate Report',
            content: 'Download the structured report with the embedded audit trail.',
            placement: 'bottom'
        }
    ];

    const AI_ASSISTANT_TOUR: TourStep[] = [
        {
            target: 'body',
            title: 'Welcome to Magnus Assistant',
            content: 'The "Jewel of the Ecosystem." The AI Assistant gives you a high-level system overview and lets you drill down instantly to extract critical intelligence and reports.',
            placement: 'center',
            page: '/ai-assistant'
        },
        {
            target: 'input[placeholder]',
            title: '1. Instant Knowledge / 40 Questions',
            content: 'Ask any of the critical 40 standard process questions. The AI will instantly search your DFMEA, PFMEA, and architecture to extract the exact information you need.',
            placement: 'top'
        },
        {
            target: 'button[type="submit"]',
            title: '2. Extract & Report',
            content: 'Submit queries to dynamically generate reports and dashboard cards that map exactly to VDA requirements.',
            placement: 'left'
        }
    ];

    const AUTO_FMEA_TOUR: TourStep[] = [
        {
            target: 'body',
            title: 'Welcome to Auto-FMEA',
            content: 'Leverage LLMs to parse manuals and ideate failure modes automatically based on historical data.',
            placement: 'center',
            page: '/auto-fmea'
        },
        {
            target: 'button:contains("Generate")',
            title: '1. Initiate Ideation',
            content: 'Click here to kick off the background AI worker to suggest new functions and failures.',
            placement: 'bottom'
        }
    ];

    const MAGNUS_GENESIS_TOUR: TourStep[] = [
        {
            target: 'body',
            title: 'Welcome to Magnus Prime',
            content: 'This is the advanced agentic workflow engine for deep-dive reliability reasoning.',
            placement: 'center',
            page: '/magnus-prime'
        },
        {
            target: 'button',
            title: '1. Agentic Pipelines',
            content: 'Trigger multi-step autonomous AI nodes for recursive refinement tasks.',
            placement: 'bottom'
        }
    ];

    const ADMIN_PORTAL_TOUR: TourStep[] = [
        {
            target: 'body',
            title: 'User Management & ISO 27001',
            content: 'Control who accesses the ecosystem. Role-Based Access Control (RBAC) is vital for secure operations.',
            placement: 'center',
            page: '/admin/users'
        },
        {
            target: 'button:contains("Invite User")',
            title: '1. Invite a New User',
            content: 'Click here to trigger the secure invitation dialog.',
            placement: 'bottom',
        },
        {
            target: 'table',
            title: '2. Review Role Provisioning',
            content: 'In this directory, you can audit permissions. Click on any user row to alter them from Read-Only to Contributor.',
            placement: 'top',
        }
    ];

    const matchTerm = (text: string) => searchTerm === '' || text.toLowerCase().includes(searchTerm.toLowerCase());

    // Search Visibility Logic
    const showLevel1 = matchTerm("1") || matchTerm("basic") || matchTerm("requirements") || matchTerm("architecture") || matchTerm("pbs") || matchTerm("project");
    const showLevel2 = matchTerm("2") || matchTerm("intermediate") || matchTerm("risk") || matchTerm("system") || matchTerm("fmea") || matchTerm("action") || matchTerm("tracking");
    const showLevel3 = matchTerm("3") || matchTerm("advanced") || matchTerm("traceability") || matchTerm("export") || matchTerm("lims") || matchTerm("physics");
    const showAI = matchTerm("4") || matchTerm("ai") || matchTerm("intelligence") || matchTerm("assistant") || matchTerm("auto-fmea") || matchTerm("prime") || matchTerm("reports") || matchTerm("40 questions");
    const showAdmin = matchTerm("5") || matchTerm("admin") || matchTerm("enterprise") || matchTerm("user") || matchTerm("audit") || matchTerm("compliance") || matchTerm("security");

    return (
        <div className={`min-h-screen ${isDark ? 'text-white' : 'text-slate-900'} p-8`}>
            {/* Hero Section */}
            <header className="relative h-[400px] overflow-hidden rounded-3xl mb-12">
                <img
                    src="/assets/images/learning-center-hero.png"
                    alt="Learning Center Hero"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/40 flex items-center px-12">
                    <div className="max-w-2xl">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 text-xs font-bold uppercase tracking-widest mb-4 border border-blue-500/30">
                                <GraduationCap size={14} /> Global Training Portal
                            </span>
                            <h1 className="text-4xl lg:text-5xl font-black mb-6 leading-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-300">
                                Accelerated Learning <br /> & Certification Paths.
                            </h1>
                            <p className="text-lg text-slate-300 mb-8 leading-relaxed max-w-xl">
                                Welcome to your structured progression journey. Complete the interactive modules below to master the Magnus Ecosystem and achieve AIAG-VDA process certification.
                            </p>

                            <div className="relative max-w-lg">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                                <input
                                    type="text"
                                    placeholder="Search modules, features, or compliance topics..."
                                    className="w-full pl-12 pr-4 py-4 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-medium shadow-2xl shadow-black/50"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </header>

            {/* PROGRESSION LEARNING PATHWAYS */}
            <div className="space-y-16 mb-20">
                {/* BASIC SECTION */}
                {showLevel1 && (
                    <div className="space-y-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-500 border border-blue-500/30">
                                <BookOpen size={20} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold">1. Basic Navigation & Setup</h3>
                                <p className="text-sm text-text-secondary font-medium">Foundational Project and Architecture Tools</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <ModuleCard
                                step={1}
                                title="Requirements & Context"
                                duration="4 min"
                                icon={<Search size={24} />}
                                colorClass="from-blue-600 to-indigo-600"
                                onClick={() => startInteractiveTour('basic-1', '/customer-requirements', DESIGN_CONTEXT_TOUR)}
                            />
                            <ModuleCard
                                step={2}
                                title="Architecture (PBS)"
                                duration="6 min"
                                icon={<Layers size={24} />}
                                colorClass="from-indigo-600 to-violet-600"
                                onClick={() => startInteractiveTour('basic-2', '/pbs-manager', PBS_MANAGER_TOUR)}
                            />
                            <ModuleCard
                                step={3}
                                title="Project Definition"
                                duration="5 min"
                                icon={<FolderTree size={24} />}
                                colorClass="from-blue-500 to-sky-600"
                                onClick={() => { navigate('/projects') }}
                            />
                        </div>
                    </div>
                )}

                {/* INTERMEDIATE SECTION */}
                {showLevel2 && (
                    <div className="space-y-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-500 border border-violet-500/30">
                                <Activity size={20} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold">2. Intermediate Risk Analysis</h3>
                                <p className="text-sm text-text-secondary font-medium">AIAG-VDA Flow - Function, Failure, and Optimization</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <ModuleCard
                                step={1}
                                title="System Definition"
                                duration="5 min"
                                icon={<Network size={24} />}
                                colorClass="from-violet-600 to-fuchsia-600"
                                onClick={() => startInteractiveTour('inter-1', '/system-definition', SYSTEM_DEFINITION_TOUR)}
                            />
                            <ModuleCard
                                step={2}
                                title="Advanced FMEA Editor"
                                duration="10 min"
                                icon={<Activity size={24} />}
                                colorClass="from-fuchsia-600 to-pink-600"
                                onClick={() => startInteractiveTour('inter-2', '/fmea-editor', FMEA_EDITOR_TOUR)}
                            />
                            <ModuleCard
                                step={3}
                                title="Action Tracking"
                                duration="6 min"
                                icon={<ListTodo size={24} />}
                                colorClass="from-pink-600 to-rose-600"
                                onClick={() => startInteractiveTour('inter-3', '/action-tracking', ACTION_TRACKING_TOUR)}
                            />
                        </div>
                    </div>
                )}

                {/* ADVANCED SECTION */}
                {showLevel3 && (
                    <div className="space-y-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-500 border border-emerald-500/30">
                                <CheckCircle2 size={20} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold">3. Advanced Traceability & Validation</h3>
                                <p className="text-sm text-text-secondary font-medium">Complex Linkages, Graph Models, and Enterprise Export</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <ModuleCard
                                step={1}
                                title="Traceability Explorer"
                                duration="8 min"
                                icon={<Share2 size={24} />}
                                colorClass="from-emerald-600 to-teal-600"
                                onClick={() => { navigate('/traceability-matrix') }}
                            />
                            <ModuleCard
                                step={2}
                                title="Enterprise Export"
                                duration="4 min"
                                icon={<Download size={24} />}
                                colorClass="from-teal-600 to-cyan-600"
                                onClick={() => startInteractiveTour('adv-2', '/export', EXPORT_TOUR)}
                            />
                            <ModuleCard
                                step={3}
                                title="LIMS / Physics Lab"
                                duration="10 min"
                                icon={<Activity size={24} />}
                                colorClass="from-cyan-600 to-blue-600"
                                onClick={() => { navigate('/lims') }}
                            />
                        </div>
                    </div>
                )}

                {/* AI INTELLIGENCE SECTION - THE JEWEL OF THE ECOSYSTEM */}
                {showAI && (
                    <div className="space-y-6 pt-8 mt-12 relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-fuchsia-500/5 to-transparent rounded-3xl -z-10 pointer-events-none" />
                        <div className="p-6 border border-purple-500/30 rounded-3xl bg-purple-500/5 shadow-[0_0_30px_rgba(168,85,247,0.15)] relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/20 blur-[80px] -z-10 rounded-full" />

                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center text-white shadow-lg shadow-purple-500/30">
                                    <Sparkles size={24} />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-2xl font-black bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-fuchsia-500">
                                            4. AI Intelligence Engines
                                        </h3>
                                        <span className="px-2.5 py-1 rounded-full bg-purple-500/20 text-purple-600 dark:text-purple-400 text-[10px] font-black uppercase tracking-widest border border-purple-500/30">
                                            Ecosystem Jewel
                                        </span>
                                    </div>
                                    <p className="text-sm text-text-secondary font-medium">Get a high-level system overview, drill down instantly, and extract the 40 standard process reports.</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                                <ModuleCard
                                    step={1}
                                    title="Magnus Assistant & 40 Qs"
                                    duration="5 min"
                                    icon={<Bot size={24} />}
                                    colorClass="from-purple-600 to-indigo-600"
                                    onClick={() => startInteractiveTour('ai-1', '/ai-assistant', AI_ASSISTANT_TOUR)}
                                />
                                <ModuleCard
                                    step={2}
                                    title="Auto-FMEA Ideation"
                                    duration="8 min"
                                    icon={<Zap size={24} />}
                                    colorClass="from-fuchsia-600 to-pink-600"
                                    onClick={() => startInteractiveTour('ai-2', '/auto-fmea', AUTO_FMEA_TOUR)}
                                />
                                <ModuleCard
                                    step={3}
                                    title="Magnus Prime Reports"
                                    duration="15 min"
                                    icon={<Cpu size={24} />}
                                    colorClass="from-sky-500 to-blue-600"
                                    onClick={() => startInteractiveTour('ai-3', '/magnus-prime', MAGNUS_GENESIS_TOUR)}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* ADMINISTRATION SECTION */}
                {showAdmin && (
                    <div className="space-y-6 pt-8 border-t border-border mt-8">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-slate-500/20 flex items-center justify-center text-slate-500 border border-slate-500/30">
                                <Settings size={20} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold">5. Enterprise Administration</h3>
                                <p className="text-sm text-text-secondary font-medium">Compliance, Access Control, and Governance</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <ModuleCard
                                step={1}
                                title="User Access Control"
                                duration="5 min"
                                icon={<Users size={24} />}
                                colorClass="from-slate-600 to-slate-800"
                                onClick={() => startInteractiveTour('admin-1', '/admin/users', ADMIN_PORTAL_TOUR)}
                            />
                            <ModuleCard
                                step={2}
                                title="Audit & Compliance"
                                duration="8 min"
                                icon={<Shield size={24} />}
                                colorClass="from-slate-500 to-slate-700"
                                onClick={() => { navigate('/admin/security') }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Ecosystem Connectivity Map */}
            <section className="mb-16">
                <div className={`p-8 rounded-3xl border bg-gradient-to-br ${isDark ? 'from-slate-800/30 to-blue-900/10 border-slate-700' : 'from-blue-50/50 to-indigo-50/50 border-slate-200'}`}>
                    <h2 className="text-2xl font-black mb-8 flex items-center gap-3">
                        <Network className="text-blue-500" />
                        Magnus Ecosystem Lifecycle Roadmap
                    </h2>

                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 relative">
                        {[
                            { app: "Magnus-Core", title: "Design Context (DFMEA)", tasks: ["Step 1-7 Compliance", "P-Diagrams", "BoM Linkage"], icon: <Cpu />, color: "border-blue-500" },
                            { app: "Magnus-PFMEA", title: "Process Control", tasks: ["Linked DFMEA Data", "Control Plans", "Work Instructions"], icon: <Settings />, color: "border-emerald-500" },
                            { app: "Magnus-Resolve", title: "Field Analysis (FRACAS)", tasks: ["8D Problem Solving", "FTA / Weibull", "Lab Results"], icon: <LineChart />, color: "border-purple-500" },
                            { app: "Magnus AI Hub", title: "Global Intelligence", tasks: ["Smart Suggestions", "Policy Enforcement", "Autonomous Auditing"], icon: <Bot />, color: "border-pink-500" }
                        ].map((item, i) => (
                            <div key={i} className={`p-5 rounded-2xl border-t-4 bg-background shadow-sm ${item.color}`}>
                                <div className="flex items-center gap-2 mb-3">
                                    <div className={`p-1.5 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>{item.icon}</div>
                                    <span className="text-xs font-black uppercase tracking-widest text-slate-500">{item.app}</span>
                                </div>
                                <h4 className="font-bold mb-3">{item.title}</h4>
                                <ul className="space-y-2">
                                    {item.tasks.map((t, idx) => (
                                        <li key={idx} className="text-xs flex items-center gap-2 text-slate-500">
                                            <CheckCircle2 size={12} className="text-blue-500" /> {t}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <div className={`mt-8 p-4 rounded-xl text-center text-sm font-medium ${isDark ? 'bg-blue-900/20 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                        💡 <strong>Concept Check:</strong> Real-world failures tracked in <strong>Resolve</strong> automatically push updates to <strong>Occurrence Ratings</strong> in the <strong>PFMEA</strong> and <strong>Core DFMEA</strong> modules.
                    </div>
                </div>
            </section>

            {/* Additional Resources */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
                {/* Webinars */}
                <section className={`p-8 rounded-3xl border ${isDark ? 'bg-slate-800/40 border-slate-700' : 'bg-card border-border shadow-sm'}`}>
                    <h3 className="text-xl font-bold mb-6 flex items-center gap-3">
                        <BookOpen className="text-pink-500" />
                        Video Knowledge Base
                    </h3>
                    <div className="space-y-4">
                        {[
                            { title: "Mastering the AIAG-VDA 5T Framework", date: "March 2026", duration: "45m" },
                            { title: "AI-Agents for Failure Mode Identification", date: "Feb 2026", duration: "1h 10m" }
                        ].map((web, i) => (
                            <div key={i} className={`flex items-center justify-between p-4 rounded-xl border border-transparent transition-colors hover:border-slate-300 dark:hover:border-slate-600 ${isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-50'}`}>
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-pink-500/10 text-pink-500 flex items-center justify-center">
                                        <Play size={16} className="fill-pink-500" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-sm">{web.title}</h4>
                                        <span className="text-xs text-slate-500">Recorded {web.date}</span>
                                    </div>
                                </div>
                                <span className="text-xs font-bold text-slate-400">{web.duration}</span>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Downloads */}
                <section className={`p-8 rounded-3xl border ${isDark ? 'bg-slate-800/40 border-slate-700' : 'bg-card border-border shadow-sm'}`}>
                    <h3 className="text-xl font-bold mb-6 flex items-center gap-3">
                        <Download className="text-emerald-500" />
                        Offline Templates
                    </h3>
                    <div className="space-y-4">
                        {[
                            { name: "Magnus AIAG-VDA 7-Step Checklist", type: "PDF", size: "1.2 MB" },
                            { name: "Project Scoping 5T Worksheet", type: "XLSX", size: "450 KB" }
                        ].map((file, i) => (
                            <div key={i} className={`flex items-center justify-between p-4 rounded-xl group border border-transparent transition-colors hover:border-slate-300 dark:hover:border-slate-600 ${isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-50'}`}>
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                                        <ClipboardCheck size={20} />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-sm">{file.name}</h4>
                                        <span className="text-xs text-slate-500">{file.type} • {file.size}</span>
                                    </div>
                                </div>
                                <Download size={18} className="text-slate-400 group-hover:text-emerald-500 transition-colors" />
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default MagnusLearningCenter;
